import { create } from 'zustand';
import { getChatbotConfigSection, updateChatbotConfigSection } from '../utils/chatbotConfigAccess';

// Settings stored in the database
interface PersistentWidgetSettings {
  publicId: string;
  showWelcomeMessage: boolean;
  showNotificationBubble: boolean;
  enableAnimation: boolean;
  customIcon: string | null;
  welcomeMessage: string;
}

// Settings only used for preview and script generation
export interface ScriptSettings {
  position: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';
  offset: { x: number; y: number };
  lang: 'fr' | 'en' | 'pt';
}

// Combined settings for UI
interface WidgetSettings extends PersistentWidgetSettings, ScriptSettings {}

const DEFAULT_PERSISTENT_SETTINGS: PersistentWidgetSettings = {
  publicId: '',
  showWelcomeMessage: true,
  showNotificationBubble: true,
  enableAnimation: true,
  customIcon: null,
  welcomeMessage: 'Hello! How can I help you today?'
};

const DEFAULT_SCRIPT_SETTINGS: ScriptSettings = {
  position: 'bottom-right',
  offset: { x: 20, y: 20 },
  lang: 'en'
};

interface WidgetSettingsStore {
  settings: WidgetSettings;
  loading: boolean;
  error: Error | null;
  initialized: boolean;
  updateSettings: (settings: Partial<PersistentWidgetSettings>, chatbotPublicId: string) => Promise<void>;
  updateScriptSettings: (settings: Partial<ScriptSettings>) => void;
  initializeSettings: (chatbotPublicId: string) => Promise<void>;
}

export const useWidgetSettings = create<WidgetSettingsStore>((set, get) => ({
  settings: { ...DEFAULT_PERSISTENT_SETTINGS, ...DEFAULT_SCRIPT_SETTINGS },
  loading: false,
  error: null,
  initialized: false,

  updateSettings: async (newSettings, chatbotPublicId) => {
    if (!chatbotPublicId) return;
    
    set({ loading: true, error: null });
    
    try {
      // Get current persisted settings
      const currentSettings = get().settings;
      
      // Merge current settings with new settings (only persistent ones)
      const updatedSettings = {
        publicId: currentSettings.publicId,
        showWelcomeMessage: currentSettings.showWelcomeMessage,
        showNotificationBubble: currentSettings.showNotificationBubble,
        enableAnimation: currentSettings.enableAnimation,
        customIcon: currentSettings.customIcon,
        welcomeMessage: currentSettings.welcomeMessage,
        ...newSettings
      };

      // Update using the centralized config access
      const success = await updateChatbotConfigSection(
        'widget_settings',
        updatedSettings,
        chatbotPublicId
      );

      if (!success) {
        throw new Error('Failed to update widget settings');
      }

      set((state) => ({
        settings: { ...state.settings, ...newSettings },
        loading: false
      }));
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Unknown error updating widget settings');
      console.error('Error updating widget settings:', error);
      set({ error, loading: false });
    }
  },

  updateScriptSettings: (newScriptSettings) => {
    // Update script settings in local state only (not persisted to DB)
    set((state) => ({
      settings: { ...state.settings, ...newScriptSettings }
    }));
  },

  initializeSettings: async (chatbotPublicId) => {
    if (!chatbotPublicId) return;
    
    set({ loading: true, error: null });
    
    try {
      // Get widget settings from the centralized config
      const widgetSettings = await getChatbotConfigSection<PersistentWidgetSettings>(
        'widget_settings',
        chatbotPublicId
      );

      if (widgetSettings) {
        set({ 
          settings: {
            // Start with default script settings that are not stored in DB
            ...DEFAULT_SCRIPT_SETTINGS,
            // Apply default persistent settings
            ...DEFAULT_PERSISTENT_SETTINGS,
            // Override with retrieved persistent settings
            ...widgetSettings,
            // Ensure publicId is always set
            publicId: widgetSettings.publicId || chatbotPublicId
          },
          initialized: true,
          loading: false
        });
      } else {
        // Use default settings if none are found
        set({ 
          settings: {
            // Default script settings
            ...DEFAULT_SCRIPT_SETTINGS,
            // Default persistent settings
            ...DEFAULT_PERSISTENT_SETTINGS,
            // Set the publicId to the chatbotPublicId if no custom one is set
            publicId: chatbotPublicId
          },
          initialized: true,
          loading: false
        });
      }
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Unknown error initializing widget settings');
      console.error('Error loading widget settings:', error);
      set({ 
        settings: {
          ...DEFAULT_SCRIPT_SETTINGS,
          ...DEFAULT_PERSISTENT_SETTINGS,
          publicId: chatbotPublicId
        },
        error,
        initialized: true,
        loading: false 
      });
    }
  }
}));